import Vue from "vue";
import VueRouter, { RouteConfig } from "vue-router";

import { LogService } from "@/services";
import { ApplicationModule, getStoreModule, RoutesModule, SessionModule } from "@/store";
import { VignetteRoutes } from "@/types";

import store from "../store";

Vue.use(VueRouter);

// Load stores
const applicationStore = getStoreModule(ApplicationModule, store);
const sessionStore = getStoreModule(SessionModule, store);
const routesStore = getStoreModule(RoutesModule, store);

// Load routes from store
const routes: RouteConfig[] = routesStore.getRoutes;

// Set app base url, used in ApiService as well
const basePath = process.env.VUE_APP_BASE_URL || "/";
// Used if origin is not available (IE11 Compatibility View)
let baseOriginFallback = window.location.protocol + "//" + window.location.host;
if (baseOriginFallback.indexOf("undefined") >= 0) {
  baseOriginFallback = "https://www.nationaljournal.com";
}
const baseOrigin = (window.location.origin || baseOriginFallback) + basePath;

LogService.info(`origin: '${window.location.origin}', fallback: '${baseOriginFallback}', baseOrigin: '${baseOrigin}'`);

applicationStore.setBaseUrlPath(basePath);
applicationStore.setBaseUrlOrigin(baseOrigin);

const router = new VueRouter({
  mode: "history",
  // App served from Django app will not be served from default path, thus need to
  // override base URL retrieved from .env.production (fallback to default for other envs)
  base: applicationStore.getBaseUrlPath,
  scrollBehavior: function (to, from, savedPosition) {
    if (to.hash) {
      return { selector: to.hash, offset: { x: 0, y: 60 } };
    } else if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  },
  routes,
});

const stripTrailingSlash = (str: string) => {
  return str.endsWith("/") ? str.slice(0, -1) : str;
};

/**
 *
 * Routing reference:
 *
 * routesStore.getPublicRoutes.map((route) => route.name)
 * ["Welcome","SignUp","Loading","NotFound","LoginRedirect","Debug"]
 *
 * routesStore.getPublicNavBarRoutes.map((route) => route.name)
 * ["Welcome","SignUp","LoginRedirect"]
 *
 * routesStore.getMemberOnlyRoutes.map((route) => route.name)
 * ["Home","Search","ProfileLanding","Profile","Briefcase","MyAccount","ProfileRequest","Loading","NotFound","Debug"]
 *
 * routesStore.getMemberOnlyNavBarRoutes.map((route) => route.name)
 * ["Home","Search","ProfileLanding","Briefcase","MyAccount","ProfileRequest"]
 *
 * Routing behaviors:
 *
 * Not logged in:
 *
 * + /Welcome, /SignUp, /NotFound, /LoginRedirect, /Debug => next()
 * + /Home, /MyAccount, /Search, /Profile, /ProfileLanding, /Briefcase, /ProfileRequest => ANON: /V/login(redir) NJ-_MBR: /SignUp (placeholder for external Marketo page)
 *
 * Logged in w/ Vignette access:
 *
 * + /NotFound, /Debug => next()
 * + /Welcome, /SignUp, , /LoginRedirect => /Home
 * + /Home, /MyAccount, /Search, /Profile, /ProfileLanding, /Briefcase, /ProfileRequest => next()
 */

/**
 *  BE SURE TO CALL next() ONLY ONCE PER beforeEach EXECUTION!!! or return immediately after calling next()
 */
router.beforeEach((to, from, next) => {
  LogService.debug(
    `router.beforeEach: from.name: '${from.name}', to.name: '${to.name}', to.fullPath: '${to.fullPath}'`
  );
  /** Intercept app loading state, redirect to Loading view */
  if (to.name !== VignetteRoutes.Loading && applicationStore.isLoading) {
    applicationStore.setRedirectPath(to.fullPath);
    LogService.debug(`isLoading, to.name: '${to.name}', next:loading`);
    next({ name: VignetteRoutes.Loading });
    return;
  } else if (to.name === VignetteRoutes.Loading) {
    LogService.debug("to:loading, next:next()");
    // gets out of if-else block, calls next() at the bottom of the function
  } else if (to.name === VignetteRoutes.MyAccount && sessionStore.hasLapsedSubscription) {
    // band aid fix for lapsed membership, no need to redirect
    // gets out of if-else block, calls next() at the bottom of the function
  } else if (
    to.name === VignetteRoutes.Profile &&
    applicationStore.getUnlockedProfiles.indexOf(parseInt(to.params.profileId)) >= 0
  ) {
    // handle unlocked profiles
    // gets out of if-else block, calls next() at the bottom of the function
  } else if (
    sessionStore.isIpUser &&
    to.name != null &&
    !routesStore.getIPUserOnlyRoutes.map((route) => route.name).includes(to.name)
  ) {
    /** isIpUser === true, navigate to Home if needed */
    LogService.warn(`ip-user, to: '${to.name}', next:PayWall`);
    next({ name: VignetteRoutes.PayWall });
    return;
  } else if (!sessionStore.hasVignetteAccess) {
    if (sessionStore.hasLapsedSubscription) {
      /** Capture lapsed members navigation and redirect to My Account for subscription options */
      /** hasVignetteAccess === false, lapsedSubscription === true, navigate to My Account if needed */
      if (to.name != null && !routesStore.getLapsedRoutes.map((route) => route.name).includes(to.name)) {
        LogService.warn(`lapsedSubscription, to: '${to.name}', next:My Account`);
        next({ name: VignetteRoutes.MyAccount });
        return;
      }
      // Continue to default next() if target page is visible to lapsed member
    } else if (to.name != null && !routesStore.getPublicRoutes.map((route) => route.name).includes(to.name)) {
      /** hasVignetteAccess === false, redirect if requested a member_only page */
      // NJ_MEMBER: Route to /SignUp
      if (sessionStore.isLoggedIn) {
        LogService.debug(`njMember, to: '${to.name}', next:SignUp`);
        next({ name: VignetteRoutes.SignUp });
        return;
      }
      // ANONYMOUS: Route to login page if targeting a member only page.
      else {
        const redirectPath = stripTrailingSlash(applicationStore.getBaseUrlPath) + to.fullPath;
        applicationStore.setRedirectPath(redirectPath);

        LogService.debug(`noAccess, to: '${to.name}', next:LoginRedirect`);
        next({ name: VignetteRoutes.LoginRedirect });
        return;
      }
      // Unreachable code - do not add anything in this level.
    }
    // Continue to default next() if target is a public page
  } else {
    /** hasVignetteAccess === true, redirect if requested a public page */
    if (to.name != null && !routesStore.getMemberOnlyRoutes.map((route) => route.name).includes(to.name)) {
      LogService.debug(`hasAccess, requested '${to.name}', next:Home`);
      next({ name: VignetteRoutes.Home });
      return;
    }
    // Continue to default next() if target is a member only page
  }

  /** All good and no need to redirect anywhere, keep going to target */
  next();
});

export default router;
